import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Lock, Eye, EyeOff, FileText, Building2, AlertTriangle, Shield, ShieldOff, Clock } from 'lucide-react';
import { checkPassword, createSession } from '@/lib/auth';
import { isLockedOut, getLockoutRemainingMs, getLoginAttempts, printSecurityWarning } from '@/lib/security';

const MAX_ATTEMPTS = 5;

interface Props { onSuccess: () => void; }

function formatCountdown(ms: number): string {
  const totalSec = Math.ceil(ms / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return `${min}:${sec.toString().padStart(2, '0')}`;
}

export default function Login({ onSuccess }: Props) {
  const [password, setPassword]     = useState('');
  const [show, setShow]             = useState(false);
  const [error, setError]           = useState('');
  const [loading, setLoading]       = useState(false);
  const [locked, setLocked]         = useState(false);
  const [countdown, setCountdown]   = useState(0);
  const [attempts, setAttempts]     = useState(0);
  const [delaying, setDelaying]     = useState(false);

  // Imprimir aviso de seguridad en consola al cargar
  useEffect(() => { printSecurityWarning(); }, []);

  // Sincronizar estado de bloqueo al montar y cada segundo durante lockout
  const syncLockState = useCallback(() => {
    if (isLockedOut()) {
      setLocked(true);
      setCountdown(getLockoutRemainingMs());
      setAttempts(MAX_ATTEMPTS);
    } else {
      setLocked(false);
      setCountdown(0);
      setAttempts(getLoginAttempts());
    }
  }, []);

  useEffect(() => { syncLockState(); }, [syncLockState]);

  useEffect(() => {
    if (!locked) return;
    const id = setInterval(() => {
      const remaining = getLockoutRemainingMs();
      if (remaining <= 0) {
        setLocked(false);
        setCountdown(0);
        setAttempts(0);
        setError('');
        clearInterval(id);
      } else {
        setCountdown(remaining);
      }
    }, 1000);
    return () => clearInterval(id);
  }, [locked]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password || locked || delaying) return;
    setLoading(true);
    setDelaying(true);
    setError('');

    const result = await checkPassword(password);
    setLoading(false);
    setDelaying(false);

    if (result.ok) {
      await createSession();
      onSuccess();
    } else if (result.locked) {
      setLocked(true);
      setAttempts(MAX_ATTEMPTS);
      setCountdown(getLockoutRemainingMs());
      setError('');
      setPassword('');
    } else {
      setAttempts(result.attempts);
      const remaining = MAX_ATTEMPTS - result.attempts;
      if (remaining <= 0) {
        setError('Demasiados intentos. Acceso bloqueado.');
      } else {
        setError(
          `Contraseña incorrecta. ${remaining} intento${remaining === 1 ? '' : 's'} restante${remaining === 1 ? '' : 's'}.`
        );
      }
      setPassword('');
    }
  };

  const attemptsLeft = MAX_ATTEMPTS - attempts;
  const showAttemptsBar = attempts > 0 && !locked;

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4">
      {/* Fondo decorativo */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: `repeating-linear-gradient(
            45deg,
            var(--color-primary) 0px,
            var(--color-primary) 1px,
            transparent 1px,
            transparent 40px
          )`,
        }}
      />

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 260, damping: 28 }}
        className="relative w-full max-w-sm"
      >
        {/* Card */}
        <div
          className="bg-card border border-border rounded-2xl p-8 shadow-lg"
          style={{ boxShadow: '0 8px 40px -8px color-mix(in srgb, var(--color-primary) 20%, transparent)' }}
        >
          {/* Logo / Brand */}
          <div className="flex flex-col items-center mb-8">
            <div
              className="h-16 w-16 rounded-2xl flex items-center justify-center mb-4"
              style={{ background: locked ? '#dc2626' : 'var(--color-primary)' }}
            >
              {locked ? <ShieldOff size={28} color="white" /> : <FileText size={28} color="white" />}
            </div>
            <h1 className="font-bold text-xl text-foreground tracking-tight text-center leading-tight">
              Presupuesto Pro
            </h1>
            <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
              <Building2 size={11} />
              Reparaciones y Reformas Oscar Alminos
            </p>
          </div>

          {/* ── BLOQUEO ACTIVO ── */}
          <AnimatePresence>
            {locked && (
              <motion.div
                key="lockout"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="mb-5 rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-center"
              >
                <div className="flex items-center justify-center gap-2 text-destructive font-semibold text-sm mb-2">
                  <AlertTriangle size={16} />
                  Acceso bloqueado temporalmente
                </div>
                <p className="text-xs text-muted-foreground mb-3">
                  Se han detectado demasiados intentos incorrectos.<br />
                  Podrás intentarlo de nuevo en:
                </p>
                <div className="flex items-center justify-center gap-2 text-2xl font-mono font-bold text-destructive">
                  <Clock size={20} />
                  {formatCountdown(countdown)}
                </div>
                <p className="text-xs text-muted-foreground mt-2">minutos : segundos</p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Formulario */}
          {!locked && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Contraseña de acceso
                </Label>
                <div className="relative">
                  <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    type={show ? 'text' : 'password'}
                    value={password}
                    onChange={e => { setPassword(e.target.value); setError(''); }}
                    placeholder="Introduce tu contraseña"
                    className={`pl-9 pr-10 ${error ? 'border-destructive' : ''}`}
                    autoFocus
                    autoComplete="current-password"
                    disabled={delaying}
                    maxLength={128}
                  />
                  <button
                    type="button"
                    onClick={() => setShow(s => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    tabIndex={-1}
                  >
                    {show ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>

                {/* Barra de intentos */}
                {showAttemptsBar && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-1">
                    <div className="flex gap-1">
                      {Array.from({ length: MAX_ATTEMPTS }).map((_, i) => (
                        <div
                          key={i}
                          className={`h-1 flex-1 rounded-full transition-all duration-300 ${
                            i < attempts
                              ? attempts >= 4 ? 'bg-destructive' : 'bg-amber-400'
                              : 'bg-muted'
                          }`}
                        />
                      ))}
                    </div>
                    <p className="text-xs text-amber-600 dark:text-amber-400">
                      {attemptsLeft === 1
                        ? '⚠ Último intento antes del bloqueo'
                        : `${attemptsLeft} intentos restantes`}
                    </p>
                  </motion.div>
                )}

                <AnimatePresence>
                  {error && (
                    <motion.p
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      className="text-xs text-destructive font-medium flex items-center gap-1"
                    >
                      <AlertTriangle size={12} /> {error}
                    </motion.p>
                  )}
                </AnimatePresence>
              </div>

              <Button
                type="submit"
                className="w-full gap-2"
                disabled={loading || delaying || !password}
              >
                {loading || delaying ? (
                  <span className="flex items-center gap-2">
                    <span className="h-4 w-4 rounded-full border-2 border-primary-foreground/40 border-t-primary-foreground animate-spin" />
                    {loading ? 'Verificando…' : 'Procesando…'}
                  </span>
                ) : (
                  <><Shield size={15} /> Acceder</>
                )}
              </Button>
            </form>
          )}

          {/* Info sesión */}
          <p className="text-center text-xs text-muted-foreground mt-5">
            Sesión válida 8 horas · Cierre automático tras 30 min sin actividad.
          </p>
        </div>

        {/* Crédito Movilizatic */}
        <p className="text-center text-xs text-muted-foreground mt-4">
          by{' '}
          <a
            href="https://movilizatic.net"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-primary transition-colors underline underline-offset-2"
          >
            movilizatic.net
          </a>
        </p>
      </motion.div>
    </div>
  );
}
